/**
 * The CDMO bulk-upload CSV contract: the exact 66 headers of `PCN_CDMO_Import.csv`, in order.
 *
 * Columns 1–28 are profile data and are imported. Columns 29–66 are the research review — when the
 * facts were collected, how, and the verification status and evidence for each group of fields.
 * They are never written to the profile; they are kept in the `cdmo_import_rows` audit record.
 *
 * A test compares this list with the template's header row, so the two cannot drift apart.
 */
export const CDMO_IMPORT_DATA_COLUMNS = [
  'company_name',
  'website',
  'cdmo_business_profile',
  'contact_name',
  'email',
  'phone',
  'secondary_emails',
  'fei_number',
  'parent_company',
  'description',
  'primary_capabilities',
  'capabilities_small_molecule',
  'capabilities_biologics',
  'capabilities_cell_gene_therapy',
  'capabilities_vaccines',
  'capabilities_medical_devices',
  'typical_batch_sizes',
  'budget_ranges',
  'target_client_types',
  'engagement_scales',
  'preferred_project_types',
  'gmp_certifications',
  'operating_regions',
  'facilities',
  'speed_to_start',
  'willingness_feasibility_studies',
  'startup_friendly',
  'admin_notes',
] as const;

export const CDMO_IMPORT_REVIEW_META_COLUMNS = [
  'research_date',
  'collected_via',
  'review_summary',
  'proposed_changes',
] as const;

/** Each group has a `<group>__status` and a `<group>__evidence` column, in this order. */
export const CDMO_IMPORT_EVIDENCE_GROUPS = [
  'cdmo_business_profile',
  'contact_details',
  'fei_number',
  'parent_company',
  'primary_capabilities',
  'detailed_capabilities',
  'typical_batch_sizes',
  'budget_ranges',
  'target_client_types',
  'engagement_scales',
  'preferred_project_types',
  'gmp_certifications',
  'operating_regions',
  'facilities',
  'speed_to_start',
  'willingness_feasibility_studies',
  'startup_friendly',
] as const;

export type CdmoImportDataColumn = (typeof CDMO_IMPORT_DATA_COLUMNS)[number];
export type CdmoImportEvidenceGroup = (typeof CDMO_IMPORT_EVIDENCE_GROUPS)[number];

export const statusColumn = (group: CdmoImportEvidenceGroup) => `${group}__status`;
export const evidenceColumn = (group: CdmoImportEvidenceGroup) => `${group}__evidence`;

export const CDMO_IMPORT_REVIEW_COLUMNS: readonly string[] = [
  ...CDMO_IMPORT_REVIEW_META_COLUMNS,
  ...CDMO_IMPORT_EVIDENCE_GROUPS.flatMap((group) => [statusColumn(group), evidenceColumn(group)]),
];

/** All 66 headers, in template order. */
export const CDMO_IMPORT_COLUMNS: readonly string[] = [
  ...CDMO_IMPORT_DATA_COLUMNS,
  ...CDMO_IMPORT_REVIEW_COLUMNS,
];

/** How each evidence group is named in messages and in the review. */
export const EVIDENCE_GROUP_LABELS: Record<CdmoImportEvidenceGroup, string> = {
  cdmo_business_profile: 'Business profile',
  contact_details: 'Contact details',
  fei_number: 'FEI number',
  parent_company: 'Parent company',
  primary_capabilities: 'Primary modalities',
  detailed_capabilities: 'Detailed capabilities',
  typical_batch_sizes: 'Typical batch sizes',
  budget_ranges: 'Budget ranges',
  target_client_types: 'Target client types',
  engagement_scales: 'Engagement scales',
  preferred_project_types: 'Preferred project types',
  gmp_certifications: 'GMP certifications',
  operating_regions: 'Operating regions',
  facilities: 'Facilities',
  speed_to_start: 'Speed to start',
  willingness_feasibility_studies: 'Feasibility studies',
  startup_friendly: 'Startup friendly',
};

const DATA_COLUMN_LABELS: Record<CdmoImportDataColumn, string> = {
  company_name: 'Company name',
  website: 'Website',
  cdmo_business_profile: 'Business profile',
  contact_name: 'Contact name',
  email: 'Email',
  phone: 'Phone',
  secondary_emails: 'Secondary emails',
  fei_number: 'FEI number',
  parent_company: 'Parent company',
  description: 'Description',
  primary_capabilities: 'Primary modalities',
  capabilities_small_molecule: 'Small Molecule capabilities',
  capabilities_biologics: 'Biologics capabilities',
  capabilities_cell_gene_therapy: 'Cell & Gene Therapy capabilities',
  capabilities_vaccines: 'Vaccines capabilities',
  capabilities_medical_devices: 'Medical Devices capabilities',
  typical_batch_sizes: 'Typical batch sizes',
  budget_ranges: 'Budget ranges',
  target_client_types: 'Target client types',
  engagement_scales: 'Engagement scales',
  preferred_project_types: 'Preferred project types',
  gmp_certifications: 'GMP certifications',
  operating_regions: 'Operating regions',
  facilities: 'Facilities',
  speed_to_start: 'Speed to start',
  willingness_feasibility_studies: 'Feasibility studies',
  startup_friendly: 'Startup friendly',
  admin_notes: 'Admin notes',
};

/**
 * Plain names for every template column, used in row messages instead of the raw header
 * (`company_name` → "Company name"). The header check still quotes the raw headers, since the
 * header row has to match them exactly.
 */
export const COLUMN_LABELS: Readonly<Record<string, string>> = {
  ...DATA_COLUMN_LABELS,
  research_date: 'Research date',
  collected_via: 'Collected via',
  review_summary: 'Review summary',
  proposed_changes: 'Proposed changes',
  ...Object.fromEntries(
    CDMO_IMPORT_EVIDENCE_GROUPS.flatMap((group) => [
      [statusColumn(group), `${EVIDENCE_GROUP_LABELS[group]} status`],
      [evidenceColumn(group), `${EVIDENCE_GROUP_LABELS[group]} evidence`],
    ])
  ),
};

export const columnLabel = (column: string): string => COLUMN_LABELS[column] ?? column;

/**
 * Groups whose facts must be confirmed from a source: only `Verified` or `Unknown / Not Verified`
 * is accepted. An inferred contact, FEI, certification or facility would be presented to sponsors
 * as fact, so the row is rejected instead.
 */
export const CDMO_IMPORT_STRICT_GROUPS: ReadonlySet<CdmoImportEvidenceGroup> = new Set<CdmoImportEvidenceGroup>([
  'contact_details',
  'fei_number',
  'parent_company',
  'primary_capabilities',
  'detailed_capabilities',
  'gmp_certifications',
  'operating_regions',
  'facilities',
]);

/** The data columns each evidence group vouches for — used to tell whether the group has data. */
export const EVIDENCE_GROUP_DATA_COLUMNS: Record<CdmoImportEvidenceGroup, readonly CdmoImportDataColumn[]> = {
  cdmo_business_profile: ['cdmo_business_profile'],
  contact_details: ['contact_name', 'email', 'phone', 'secondary_emails'],
  fei_number: ['fei_number'],
  parent_company: ['parent_company'],
  primary_capabilities: ['primary_capabilities'],
  detailed_capabilities: [
    'capabilities_small_molecule',
    'capabilities_biologics',
    'capabilities_cell_gene_therapy',
    'capabilities_vaccines',
    'capabilities_medical_devices',
  ],
  typical_batch_sizes: ['typical_batch_sizes'],
  budget_ranges: ['budget_ranges'],
  target_client_types: ['target_client_types'],
  engagement_scales: ['engagement_scales'],
  preferred_project_types: ['preferred_project_types'],
  gmp_certifications: ['gmp_certifications'],
  operating_regions: ['operating_regions'],
  facilities: ['facilities'],
  speed_to_start: ['speed_to_start'],
  willingness_feasibility_studies: ['willingness_feasibility_studies'],
  startup_friendly: ['startup_friendly'],
};

/** Detailed-capability column → the `detailed_capabilities` key (primary modality) it fills. */
export const DETAILED_CAPABILITY_COLUMNS = {
  capabilities_small_molecule: 'Small Molecule',
  capabilities_biologics: 'Biologics',
  capabilities_cell_gene_therapy: 'Cell & Gene Therapy',
  capabilities_vaccines: 'Vaccines',
  capabilities_medical_devices: 'Medical Devices',
} as const satisfies Partial<Record<CdmoImportDataColumn, string>>;

export type DetailedCapabilityColumn = keyof typeof DETAILED_CAPABILITY_COLUMNS;

// The review columns' allowed values live in src/constants with every other option list; re-exported
// here so the importer keeps one place to read the CSV contract from.
export {
  COLLECTED_VIA_OPTIONS,
  EVIDENCE_STATUS,
  EVIDENCE_STATUS_OPTIONS,
  type EvidenceStatus,
} from '../../constants/cdmoImport';

/**
 * Extra columns the downloadable error report appends. A re-uploaded report may still carry them at
 * the end; they are ignored so the fixed file can be uploaded as it is.
 */
export const ERROR_REPORT_COLUMNS = ['import_errors', 'import_warnings'] as const;

/** Separator for multi-value cells. */
export const MULTI_VALUE_SEPARATOR = '|';

export const CDMO_IMPORT_MAX_ROWS = 500;
export const CDMO_IMPORT_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const CDMO_IMPORT_MAX_FEI_NUMBERS = 10;
/** The profile form has four additional-email slots; a fifth would be dropped the next time the profile is saved. */
export const CDMO_IMPORT_MAX_SECONDARY_EMAILS = 4;
export const CDMO_IMPORT_MAX_FACILITIES = 100;
export const CDMO_IMPORT_DESCRIPTION_MAX_LENGTH = 2000;
/** Same limits the profile form applies to a facility's free-text fields. */
export const CDMO_IMPORT_FACILITY_LOCATION_MAX_LENGTH = 200;
export const CDMO_IMPORT_FACILITY_EQUIPMENT_MAX_LENGTH = 2000;

/** Keys a facility object in the `facilities` JSON must have — no more, no fewer. */
export const FACILITY_JSON_KEYS = ['region', 'location', 'capabilities', 'equipment'] as const;

/** Path of the blank template served from `public/`. */
export const CDMO_IMPORT_TEMPLATE_PATH = '/templates/PCN_CDMO_Import.csv';

/**
 * The company name of the template's example row, which starts "EXAMPLE —" and ends "(delete this
 * row)". The word alone does not identify it: real companies are named "Example …" too.
 */
export const CDMO_IMPORT_EXAMPLE_ROW_NAME = /(?:^example(?:\s*[—–]|\s+-\s))|(?:\(delete this row\))/i;
